import React, { useContext, useEffect, useRef } from "react";
import { useState } from "react";
import {
  Divider,
  Button,
  Popper,
  ClickAwayListener,
  Paper,
} from "@mui/material";
import { ColorPicker, useColor, } from "react-color-palette";
import "react-color-palette/lib/css/styles.css";
import "react-phone-number-input/style.css";
import { useLocation, useParams } from "react-router-dom";  
import BookingBasicPart1 from "./bookingBasicPart1";
import BookingBasicPart2 from "./bookingBasicPart2";
import CustomDialog from "./useCloseConfirmation";
import { useBookingTypeContext } from "./bookingTypeProvider";
import { useSelectedBusiness } from "../../../redux/store";
import { Recurring } from "../../../types";
import { UserContext } from "../../auth/auth-context";
import { getValueFromKey, MeetingOptions, MeetingTypes } from "../../../util/meetingTypes";
import AccessRestrictedButton from "../../../util/accessRestrictedButton";
import GenericTooltip from "../../common/genericToolTip";
import { zonedTimeToUtc } from "date-fns-tz";
import { endOfDay, addDays, startOfDay } from "date-fns";
import UpgradePlan from "../../../util/upgrade/upgradePlan";
import { getEffectivePlan } from "../../../util/utils";
import { colorNameToHex } from "../../../util/colorMap";

 
const tooltipData = {
  name: "Enter a name for your bookingType.",
  location: "Enter a location for your bookingType.",
  description: "Enter a description for your bookingType.",
  link: "Enter a bookingType link that describes your bookingType.",
  color: "Choose a color for your bookingType.",
  maxInvitees: "Maximum number of invitees that can join at a time.",
  approval: "Decide whether you want to approve any meeting automatically or wait for your approval",
};

// Usage example
<GenericTooltip
  title={tooltipData.name}
// other props
/>

const BookingBasic = () => {
  const {
    isBookingBasicDialogOpen,
    setIsBookingBasicDialogOpen,
    handleBookingBasicCloseDialog,
    showBookingBasicDialogSection,
    props,
    handleSubmit,
    bookingType,
  } = useBookingTypeContext()

  const { id, bookingtypecategory } = useParams();
  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);      
  const location = useLocation(); // Get full URL path 
  const isUpdateMode = location.pathname.includes("/update/");
  const bookingTypeId = bookingType?.id
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;
  const selectedBusiness = useSelectedBusiness();
  const businessStaffs = selectedBusiness?.businessStaffs;
  const [colorPickerPopper, setColorPickerPopper] = useState(false);
  const [colorPicker, setColorPicker] = useColor("hex", "#121212");
  const [name, setName] = useState(bookingType.title || "");
  const [meetingLocationTypeKey, setMeetingLocationTypeKey] = useState(bookingType?.meetingLocationTypeId || "");
  const [desc, setDesc] = useState(bookingType.desc || "");
  const optionsSelect = ["15 min", "30 min", "45 min", "60 min", "Custom"];
  const optionsRefer = [15, 30, 45, 60];
  const [bookingTypeLength, setBookingTypeLength] = useState(
    bookingTypeId
      ? optionsRefer.includes(bookingType.length)
        ? optionsSelect[optionsRefer.indexOf(bookingType.length)]
        : "Custom"
      : "30 min"
  );

  const [hrsOmins, setHrsOMins] = useState(
    bookingTypeId ? bookingType.length : 0
  );
  const [locationTypeInWord, setLocationType] = useState(
    bookingTypeId ? getValueFromKey(bookingType.meetingLocationTypeId) : ""
  );
  const [phoneNumber, setPhoneNumber] = useState(
    bookingTypeId ? bookingType.phoneNumber : ""
  );
  const [canInviteeCall, setCanInviteeCall] = React.useState(
    bookingTypeId ? !!bookingType?.canInviteeCall : false
  );

  const businessBookingTypes = selectedBusiness?.businessBookingTypes;

  const selectedBookingType = businessBookingTypes?.find(
    (bookingType) => Number(bookingType.id) === Number(props.id)
  );

  const [typeOTime, setType] = useState("min");

  const [assignedStaffs, setAssignedStaffs] = useState([]);
  const [custom, setCustom] = useState(false);
  const getDefaultGroupId = (staffId) => {
    const targetStaff = businessStaffs?.find(
      (staff) => Number(staff.id) === Number(staffId)
    );
    const matchingGroup = targetStaff?.workingHourGroups.find(
      (workingHourGroup) => workingHourGroup.def === true
    );

    return Number(matchingGroup?.id);
  };

  const bookingCategoryMap = {
    event: 1,
    service: 2,
    class: 3,
  };

  const getBookingCategory = (input) => {
    if (typeof input === "string") {
      return bookingCategoryMap[input.toLowerCase()] || 0;
    } else if (typeof input === "number") {
      for (const category in bookingCategoryMap) {
        if (bookingCategoryMap[category] === input) {
          return category;
        }
      }
      return "";
    } else {
      return "";
    }
  };


  function getSelectedGroupIdForStaff(staffId) {
    let defaultGroupId = null;
    var selectedStaff = null;
    if (isUpdateMode) {
      selectedStaff = selectedBookingType?.assignedStaffs?.find(
        (staff) => Number(staff.id) === Number(staffId)
      );
      if (selectedStaff) return Number(selectedStaff.groupId);
    }

    defaultGroupId = getDefaultGroupId(staffId);

    return Number(defaultGroupId);
  }

  const [groupId, setGroupId] = useState(
    getSelectedGroupIdForStaff(providerUser.staffId)
  );

  const [bookingTypeCategoryId, setBookingTypeCategoryId] = useState(
    bookingTypeId 
      ? bookingType.bookingTypeCategoryId
      : getBookingCategory(bookingtypecategory)
  );

  const handleChange = (setter1, setter2) => (event) => {
    setter1(event.target.value);
    setter2 && setter2(event.target.value);
  };




  useEffect(() => {
    const existingStaffs = selectedBookingType?.assignedStaffs?.map(
      (assignedStaff) => ({
        staffId: Number(assignedStaff.staffId),
        groupId: Number(assignedStaff.groupId),
        permissionId: Number(assignedStaff?.permissionId),
      })
    );
    if (existingStaffs?.length > 0) {
      setAssignedStaffs(existingStaffs);
    } else {
      setAssignedStaffs([
        {
          staffId: Number(providerUser.staffId),
          groupId: custom
            ? groupId
            : getSelectedGroupIdForStaff(providerUser.staffId)
        },
      ]);
    }
  }, []);



  // useEffect(() => {
  //   if (!link && !bookingType?.link) {
  //     setLink(name.toLowerCase().replace(/\s+/g, '-'));
  //   }
  // }, [name, bookingType?.link]); 


  const getDefaultLocationInput = (selectedBusiness, key) => {
    if (key === MeetingTypes.IN_PERSON.key) {
      return selectedBusiness.businessBasic?.address ?? ""
    }
  }

  const [locationInput, setLocationInput] = useState(
    bookingType && bookingType.meetingLocationTypeId !== meetingLocationTypeKey
      ? getDefaultLocationInput(selectedBusiness, meetingLocationTypeKey)
      : bookingType
        ? bookingType.location
        : ""
  );

  const [color, setColor] = useState(bookingTypeId ? bookingType.color : "red");
  const initialColors = Object.keys(colorNameToHex).map((key, ind) => {
    const push = { color: key };
    if (Object.keys(colorNameToHex).length - 1 === ind) {
      push.type = "rgb";
    }
    return push;
  });
  const [colors, setColors] = useState(initialColors);


  useEffect(() => {
    if (bookingType && colors.length > 0) {
      const updatedColors = [...colors];
      const colorIndex = updatedColors.findIndex((c) => c.color === (color?.includes("rgb") ? "rgb" : color));
      if (colorIndex !== -1) {
        updatedColors[colorIndex] = { ...updatedColors[colorIndex], selected: true };
      }
      setColors(updatedColors);
    }
  }, [bookingType, color]);

  const standardPlanRequired = 2
  const planRequirements = {
    link: standardPlanRequired,
    maxNum: standardPlanRequired,
    remaining: standardPlanRequired,
    price: standardPlanRequired,
    isPaymentRequired: standardPlanRequired,
    bookingSize: standardPlanRequired
  };

  const handleBookingDetailChange = (key, value) => {
    // Calculate the effective plan from providerUser values.
    const effectivePlan = getEffectivePlan(providerUser) 

    // If a plan requirement exists for the given key...
    if (planRequirements[key] !== undefined) {
      // If the effective plan is less than the required plan for this field,
      // trigger the upgrade check.
      if (effectivePlan < planRequirements[key]) {
        // Only trigger if not already triggered.
        if (!checkPlanNeeded) {
          setCheckPlanNeeded(true);
        }
      return; // Prevent updating state if plan is insufficient.
    }
    }
    // Otherwise, update the bookingDetails state.
    setBookingDetails(prevDetails => ({ ...prevDetails, [key]: value }));
  };

  const [bookingDetails, setBookingDetails] = useState({ 
    bookingSize: bookingType?.bookingSize ?? "one",
    link: bookingTypeId ? bookingType.link : "",
    maxNum: bookingType?.maxInvitees ? bookingType.maxInvitees : 1,
    remaining: bookingType?.showRemaining ? bookingType.showRemaining : 0,
    price: bookingType?.price ? bookingType.price : "",
    isPaymentRequired: bookingType?.isPaymentRequired ? bookingType.isPaymentRequired : false,
  });

  useEffect(() => {
    if (bookingType && bookingType.meetingLocationTypeId !== meetingLocationTypeKey) {
      setLocationInput(selectedBusiness?.businessBasic?.address ?? "");
    } else if (bookingType) {
      setLocationInput(bookingType.location);
    } else if (meetingLocationTypeKey === MeetingTypes.IN_PERSON.key) {
      setLocationInput(selectedBusiness?.businessBasic?.address ?? "");
    } else {
      setLocationInput("");
    }
  }, [bookingType, meetingLocationTypeKey, selectedBusiness]);

  const handleInputChange = (e) => {
    setLocationInput(e.target.value)
  };


  const isFormValid = () => {
    // Booleans for each validation check
    const nameError = !name;
    const locationTypeError = !locationTypeInWord;
    const locationInputError = (meetingLocationTypeKey === MeetingTypes.IN_PERSON.key) && !locationInput;

    // Create an array to match your existing error structure
    const errors = [
      nameError,
      locationTypeError,
      locationInputError,
    ];
    // Return whether all are valid
    return errors.every((err) => !err);
  };


  const bookingPart1 = <BookingBasicPart1
    inputRef={null} // You can pass input refs if required
    isDisabled={false} // Example of a disabled state 
    name={name}
    bookingTypeLength={bookingTypeLength}
    setBookingTypeLength={setBookingTypeLength}
    optionsSelect={optionsSelect}
    hrsOmins={hrsOmins}
    setHrsOMins={setHrsOMins}
    typeOTime={typeOTime}
    setType={setType}
    options={MeetingOptions}
    meetingLocationTypeKey={meetingLocationTypeKey}
    setMeetingLocationTypeKey={setMeetingLocationTypeKey}
    setLocationType={setLocationType} // Assuming location type is the same as the key
    MeetingTypes={MeetingTypes}
    locationInput={locationInput}
    setLocationInput={setLocationInput}
    tooltipData={tooltipData}
    desc={desc}
    setDesc={setDesc}
    setName={setName}
    setBookingTypeCategoryId={setBookingTypeCategoryId}
    bookingTypeCategoryId={bookingTypeCategoryId}
    handleInputChange={handleInputChange}
    canInviteeCall={canInviteeCall}
    setCanInviteeCall={setCanInviteeCall}
    phoneNumber={phoneNumber}
    setPhoneNumber={setPhoneNumber}
    handleChange={handleChange}
    colorsMap={colorNameToHex}
    color={color}
    setColor={setColor}
    colors={colors} 
  />
  // Custom actions for the dialog
  const customActions = (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "20px",
        }}
      >
        {bookingTypeId ? (
          <AccessRestrictedButton disabled={false} onClick={() => {
            isFormValid() && handleSubmit({
              bookingTypeId: parseInt(bookingTypeId),
              title: name,
              desc,
              color,
              length:
                hrsOmins && bookingTypeLength === "Custom"
                  ? hrsOmins * (typeOTime === "min" ? 1 : 60)
                  : parseInt(bookingTypeLength.slice(0, 2)),
              location: locationInput,
              meetingLocationTypeId: meetingLocationTypeKey,
              phoneNumber: canInviteeCall ? phoneNumber : null,
              canInviteeCall: canInviteeCall,
              link: bookingDetails.link,
              price: parseFloat(bookingDetails.price),
              isPaymentRequired: Boolean(bookingDetails.isPaymentRequired),
              showRemaining: Boolean(bookingDetails.remaining),
              maxInvitees: Number(bookingDetails.maxNum),
              bookingSize: bookingDetails.bookingSize,
              DialogOpen: setIsBookingBasicDialogOpen,
              bookingTypeCategoryId: bookingTypeCategoryId,
            });
          }}>
            Save & Close
          </AccessRestrictedButton>

        ) : (
          // TODO: default most of them at server level
          <AccessRestrictedButton disabled={!isFormValid()} onClick={() => handleSubmit({
            bookingTypeId: parseInt(bookingTypeId) ?? null,
            title: name,
            // location: props.locationInput,
            desc: desc,
            color: color,
            link: name.toLowerCase().replace(/\s+/g, '-'), 
            location: locationInput,
            recurring: Recurring.NONE, 
            meetingLocationTypeId: meetingLocationTypeKey,
            phoneNumber: canInviteeCall ? phoneNumber : null,
            canInviteeCall: canInviteeCall,
            // bookingPerDay: bookingPerDay ? parseInt(bookingPerDay) : null, 
            // image,
            start: zonedTimeToUtc(startOfDay(new Date()), providerUser?.timeZone?.timezone),
            end: zonedTimeToUtc(endOfDay(addDays(new Date(), 30)), providerUser?.timeZone?.timezone),
            length: hrsOmins && bookingTypeLength === "Custom"
              ? hrsOmins * (typeOTime === "min" ? 1 : 60)
              : parseInt(bookingTypeLength.slice(0, 2)),
            bookingTypeCategoryId: bookingTypeCategoryId,
            assignedStaffs,
            createdBy: providerUser.staffId,
          })}>
            Save
          </AccessRestrictedButton>

        )}

      </div>
    </>
  );

  return (
    <CustomDialog openDialog={isBookingBasicDialogOpen} setOpenDialog={setIsBookingBasicDialogOpen} onDialogClose={handleBookingBasicCloseDialog} actions={customActions}>
      <>
        {checkPlanNeeded && (
          <UpgradePlan
            featureName={"Group related"}
            requiredPlanId={standardPlanRequired}
            onClose={(skipUpgrade) => {
              setCheckPlanNeeded(false);
              // if (skipUpgrade) {
              //  setCheckPlanNeeded(true);
              // }
            }}
          />
        )}

        <div>
          <div>
            <Popper
              open={colorPickerPopper}
              placement="bottom-start"
            >
              {" "}
              <ClickAwayListener
                onClickAway={(e) => {
                  e.stopPropagation();
                  setColorPickerPopper(false);
                }}
              >
                <>
                  <ColorPicker
                    width={300}
                    height={150}
                    color={colorPicker}
                    onChange={(e) => {
                      setColor(e.hex);
                      setColorPicker(e);
                    }}
                    hideHSV
                    hideRGB
                    hideHEX
                    dark
                  />
                  <Divider />
                  <Paper
                    style={{
                      height: "30px",
                      display: "flex",
                      justifyContent: "flex-end",
                      padding: "10px",
                    }}
                  >
                    <Button
                      style={{ background: "#d9bca9" }}
                      variant="contained"
                      onClick={() => setColorPickerPopper(false)}
                    >
                      Finish
                    </Button>
                  </Paper>
                </>
              </ClickAwayListener>
            </Popper>
            {showBookingBasicDialogSection === "basic" && (
              bookingPart1
            )}
            {showBookingBasicDialogSection === "moreOptions" && <BookingBasicPart2 bookingDetails={bookingDetails} handleBookingDetailChange={handleBookingDetailChange}
              handleInputChange={handleInputChange}
            />
            }

          </div>
        </div>

      </>
    </CustomDialog>
  );
};

export default BookingBasic;
